import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import type { TicketOption } from './ticket.types'
import { formatPrice, getStatusText } from './ticket.utils'
import { useTranslate } from '@repo/unicity'

export interface TicketCardProps {
    ticket: TicketOption
    isSelected: boolean
    onSelect: (ticketId: string) => void
    isEligible?: boolean
}

const TicketCard = ({ ticket, isSelected, onSelect, isEligible = true }: TicketCardProps) => {
    const canSelect = ticket.status === 'available' && isEligible
    const { translate } = useTranslate()

    const handleClick = () => {
        if (canSelect) {
            onSelect(ticket.item_code)
        }
    }

    return (
        <Box
            onClick={handleClick}
            sx={{
                padding: '22px 24px',
                bgcolor: isSelected ? 'white' : 'rgba(255, 255, 255, 0.5)',
                borderRadius: 2,
                cursor: canSelect ? 'pointer' : 'default',
                borderWidth: isSelected ? '2px' : '1px',
                borderStyle: 'solid',
                borderColor: isSelected ? 'rgba(90, 143, 195, 0.6)' : 'secondary.light',
                boxShadow: 'none'
            }}>
            <Stack direction="column" justifyContent="space-between" gap={1}>
                {/* Top row - Name & Price */}
                <Stack justifyContent="space-between" direction="row" alignItems="center" gap={1} display={'grid'} gridTemplateColumns={'3fr 1fr'}>
                    <Typography
                        variant="h3"
                        sx={{
                            color: 'primary.main',
                            fontWeight: isSelected ? 700 : 400
                        }}>
                        {ticket.ticket_type}
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: 'warning.main',
                            fontSize: '18px',
                            textAlign: 'right'
                        }}>
                        {formatPrice(ticket.price, ticket.currency)}
                    </Typography>
                </Stack>

                {/* Bottom row - Valid dates & Status */}
                <Stack justifyContent="space-between" direction="row" alignItems="center">
                    <Typography
                        variant="caption2"
                        sx={{
                            color: 'primary.light'
                        }}>
                        {translate('valid')}: {translate(ticket.period_text)}
                    </Typography>

                    <Typography
                        variant="caption"
                        fontSize={'12px'}
                        sx={{
                            color: 'primary.main'
                        }}>
                        {translate(getStatusText(ticket.status))}
                    </Typography>
                </Stack>
            </Stack>
        </Box>
    )
}

export default TicketCard
